<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import NoteConfigButton from "../Defaults/NoteConfigButton.vue";
import NoteMenuState from "@/stores/NoteMenuState";
import { DRAGGER_ROW_HEIGHT, Z_INDEX_TOOLBAR } from "./constants";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import {
  activeCatalogue,
  activeCatalogueNotes,
} from "@/stores/CatalogueTabsState";
import { headerHeightPx, isDraggerRowOpen } from "@/stores/CanvasLayoutState";
import { getCanvasIconScale } from "@/composable/useCanvasUiScale";
import { useHorizontalSwipe } from "@/composable/useHorizontalSwipe";

/**
 * Ligne des notes du catalogue ouvert, sous l'en-tête, sur toute la largeur :
 * les barres latérales commencent en dessous. Se replie quand aucun catalogue
 * n'est ouvert (onglet ouvert touché une 2e fois).
 *
 * Défilement : au doigt, glisser horizontalement fait défiler (glisser vers le
 * bas attrape la note) ; à la souris, flèches et molette.
 */

// ── Scroll / caret logic ──────────────────────────────────────────────────────
const contentRef = ref<HTMLElement | null>(null);
const stripRef = ref<HTMLElement | null>(null);
const canScrollBack = ref(false);
const canScrollForward = ref(false);

const swipe = useHorizontalSwipe(contentRef);

function updateScrollState() {
  const el = contentRef.value;
  if (!el) return;
  // Tolérance d'1 px : avec le zoom de l'interface, scrollLeft peut être fractionnaire.
  canScrollBack.value = el.scrollLeft > 1;
  canScrollForward.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
}

function scrollByPage(direction: -1 | 1) {
  const el = contentRef.value;
  if (!el) return;
  swipe.stopInertia();
  el.scrollBy({ left: direction * el.clientWidth * 0.6, behavior: "smooth" });
}

/** Molette verticale d'une souris sans défilement horizontal. */
function onWheel(e: WheelEvent) {
  const el = contentRef.value;
  if (!el || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
  swipe.stopInertia();
  el.scrollLeft += e.deltaY;
  e.preventDefault();
}

let resizeObserver: ResizeObserver | null = null;

function observe() {
  resizeObserver?.disconnect();
  if (contentRef.value) resizeObserver?.observe(contentRef.value);
  if (stripRef.value) resizeObserver?.observe(stripRef.value);
  updateScrollState();
}

onMounted(() => {
  resizeObserver = new ResizeObserver(updateScrollState);
  observe();
});

// La ligne est démontée quand elle se replie : on ré-observe à sa réouverture.
watch([contentRef, stripRef], observe, { flush: "post" });

// Nouveau catalogue : on repart du début de la ligne.
watch(activeCatalogue, async () => {
  // Un élan en cours écraserait le retour au début.
  swipe.stopInertia();
  await nextTick();
  if (contentRef.value) contentRef.value.scrollLeft = 0;
  updateScrollState();
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});
</script>

<template>
  <div
    v-if="isDraggerRowOpen"
    class="position-absolute dragger-row canvas-icon-scaled"
    :style="{
      top: `${headerHeightPx / getCanvasIconScale()}px`,
      left: 0,
      right: 0,
      height: `${DRAGGER_ROW_HEIGHT}px`,
      zIndex: Z_INDEX_TOOLBAR,
    }"
    @click.stop
  >
    <div
      ref="contentRef"
      class="dragger-scroller"
      @scroll="updateScrollState"
      @wheel="onWheel"
      @pointerdown.capture="swipe.onPointerDown"
      @pointermove="swipe.onPointerMove"
      @pointerup="swipe.onPointerUp"
      @pointercancel="swipe.onPointerCancel"
    >
      <div ref="stripRef" class="dragger-strip">
        <NoteConfigButton
          v-for="config in activeCatalogueNotes"
          :key="config.id"
          :config="config"
          :activeNoteConfig="NoteMenuState.activeNote"
          :height="`${DRAGGER_ROW_HEIGHT - 8}px`"
        />
      </div>
    </div>

    <!-- Flèches : souris seulement (au doigt, on fait glisser la ligne) -->
    <button
      v-show="canScrollBack"
      type="button"
      class="canvas-btn scroll-arrow scroll-arrow-start"
      :aria-label="$t('canvas:header.draggers.scrollBack')"
      @click="scrollByPage(-1)"
    >
      <PhosphorIcon name="caret-left" size="16" />
    </button>
    <button
      v-show="canScrollForward"
      type="button"
      class="canvas-btn scroll-arrow scroll-arrow-end"
      :aria-label="$t('canvas:header.draggers.scrollForward')"
      @click="scrollByPage(1)"
    >
      <PhosphorIcon name="caret-right" size="16" />
    </button>
  </div>
</template>

<style lang="scss" scoped>
.dragger-row {
  display: flex;
  align-items: stretch;
  background-color: var(--canvas-bg);
  border-bottom: 1px solid var(--canvas-border);
}

.dragger-scroller {
  flex: 1 1 auto;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  // Pas de défilement natif : il est fait en JS (useHorizontalSwipe), pour que
  // le même geste ne serve pas à la fois à défiler et à glisser une note.
  touch-action: none;
  overscroll-behavior-x: contain;

  &::-webkit-scrollbar {
    display: none;
  }
}

.dragger-strip {
  display: flex;
  align-items: center;
  gap: 2px;
  width: max-content;
  height: 100%;
  padding: 0 4px;

  > * {
    flex-shrink: 0;
  }
}

// Posées sur les bords de la ligne, avec un fondu, pour ne pas décaler les notes.
.scroll-arrow {
  display: none;
  position: absolute;
  top: 0;
  bottom: 0;
  width: 32px;
  height: auto;
  aspect-ratio: auto;
  border-radius: 0;
  background: var(--canvas-bg);

  &.scroll-arrow-start {
    left: 0;
    box-shadow: 8px 0 10px var(--canvas-bg);
  }

  &.scroll-arrow-end {
    right: 0;
    box-shadow: -8px 0 10px var(--canvas-bg);
  }
}

@media (hover: hover) and (pointer: fine) {
  .scroll-arrow {
    display: flex;
  }
}
</style>
